import * as Sentry from '@sentry/react';
import React from 'react';
import { replayIsExcluded, replaySampleRates } from 'Utils/sentryReplay';
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType
} from 'react-router';

const SENTRY_DSN =
    'https://350cb74546056593c7daa00926bda1e2@o4506767809708032.ingest.sentry.io/4506768193224704';

// Map REACT_APP_ENVIRONMENT onto the same Sentry environment names used by
// backend issue alerts (`prod` / `staging` / `demo`). The JS SDK otherwise
// defaults the official environment field to `production`, which is what
// alert rules filter on — not the custom tag.
function mapSentryEnvironment(environment) {
    if (environment === 'test' || environment === 'design') {
        return 'staging';
    }
    if (environment === 'prod') {
        return 'prod';
    }
    return environment;
}

// Enabled by default so new deployment environments (e.g. preprod-us1) are
// covered automatically; only skip local dev environments that have no
// meaningful Sentry project association.
export function initSentry(environment) {
    // Fail closed when the deploy omitted REACT_APP_ENVIRONMENT: do not let
    // the SDK default to `production`, which is not in our alert filters.
    if (!environment || environment === 'local' || environment === 'docker') {
        return;
    }

    const sentryEnvironment = mapSentryEnvironment(environment);
    const replayRates = replaySampleRates();

    Sentry.init({
        dsn: SENTRY_DSN,
        environment: sentryEnvironment,
        integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect: React.useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes
            }),
            Sentry.replayIntegration({
                beforeErrorSampling: () => !replayIsExcluded()
            })
        ],
        tracesSampleRate: 1.0,
        tracePropagationTargets: ['localhost'],
        // 10% of sessions, plus every session that hits an error.
        // unixi.io addresses are 0 — see replaySampleRates().
        ...replayRates
    });

    Sentry.setTag('environment', sentryEnvironment);
}
